<template>
  <div class="history-container">
    <LoadingComp :visible="loading" text="Đang xác thực captcha..." />
    <div v-if="!searchStore.query">
      <p>
        Chưa có thông tin tìm kiếm. Vui lòng quay lại trang chủ để tìm theo SĐT.
      </p>
      <button class="btn btn-primary btn-back" @click="$router.back">
        Về trang chủ
      </button>
    </div>
    <div 
      v-else 
      class="table-wrapper"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseUp"
    >
      <table class="history-table">
        <thead>
          <tr>
            <th class="text-center">Thời gian đặt</th>
            <th class="text-center">Thời gian hẹn</th>
            <th style="padding-left: 10px">Khách hàng</th>
            <th>Thú cưng</th>
            <th>Yêu cầu</th>
            <th class="text-center">Mã QR</th>
            <th>Trạng thái</th>
          </tr>
        </thead>
      <tbody>
        <template v-if="history.length > 0">
          <tr v-for="(item, index) in history" :key="index">
            <td class="text-center">{{ formatDate(item.createdAt) }}</td>
            <td class="text-center">
              {{ item.startTime }} {{ formatDayOnly(item.date) }}
            </td>
            <td style="padding-left: 10px; position: relative; top: -4px">
              <div class="customer-info">
                {{ item.userName }} - {{ item.userPhone }}
                <span v-if="item.isUpdated" class="update-text"
                  >Đã cập nhật</span
                >
              </div>
              <p>{{ item.fullAddress }}</p>
              <p class="customer-note"><span>Ghi chú: </span>{{ item.note }}</p>
            </td>
            <td>
              <div>
                {{ item.petName }} - {{ item.petType }} - {{ item.petBreed }}
              </div>
            </td>
            <td>{{ item.requests.join(", ") }}</td>
            <td class="qr-cell">
              <div v-if="item.qrDataUrl" class="qr-wrapper">
                <img
                  :src="item.qrDataUrl"
                  alt="QR Code"
                  class="qr-image"
                />
                <a
                  href="javascript:void(0)"
                  class="qr-download"
                  @click.prevent="downloadQR(item.qrDataUrl, item.appointmentId)"
                >
                  Tải về
                </a>
              </div>
              <span v-else>-</span>
            </td>
            <td :class="`status-${item.status.toLowerCase()}`">
              {{ getTextStatus(item.status) }}
            </td>
          </tr>
        </template>
        <tr v-else>
          <td colspan="7" class="text-center">Chưa có lịch hẹn nào</td>
        </tr>
      </tbody>
      </table>
    </div>

    <div class="history-mobile">
      <template v-if="history.length > 0">
        <div
          v-for="(item, index) in history"
          :key="index"
          class="history-mobile-item"
        >
          <p>
            {{ formatDate(item.createdAt) }} - {{ item.startTime }}
            {{ formatDayOnly(item.date) }}
          </p>
          <p>{{ item.userName }} - {{ item.userPhone }}</p>
          <ul style="margin-bottom: 0">
            <li>
              <span class="mobile-label">Thú cưng: </span> {{ item.petName }} -
              {{ item.petType }} - {{ item.petBreed }}
            </li>
            <li>
              <span class="mobile-label">Yêu cầu: </span>
              {{ item.requests.join(", ") }}
            </li>
            <li><span class="mobile-label">Ghi chú: </span>{{ item.note }}</li>
            <li>
              <span class="mobile-label">Địa chỉ: </span>{{ item.address }}
            </li>
          </ul>
        </div>
      </template>
      <div v-else>
        <p class="text-center">Chưa có lịch hẹn nào</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useSearchStore } from "../stores/search.store";
import { STATUS, ANIMAL_TYPE } from "../constants/constants";
import CategoryService from "../services/category.service";
import AppointmentService from "../services/appointment.service";
import { TABLE_NAME } from "../constants/tableName";
import { format } from "date-fns";
import QRCode from "qrcode";
import LoadingComp from "../components/LoadingComp.vue";

export default {
  name: "HistoryView",
  components: { LoadingComp },
  setup() {
    const searchStore = useSearchStore();
    return { searchStore };
  },
  data() {
    return {
      history: [],
      petBreedData: [],
      requestData: [],
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      loading: false,
    };
  },
  created() {
    this.initData();
  },
  methods: {
    async initData() {
      await this.getBreedData();
      await this.getRequestData();
      await this.getHistory();
    },
    async getBreedData() {
      this.petBreedData = await CategoryService.getDataByCollectionName(
        TABLE_NAME.PET_BREED
      );
    },
    async getRequestData() {
      this.requestData = await CategoryService.getDataByCollectionName(
        TABLE_NAME.APPOINTMENT_REQUEST
      );
    },

    async getHistory() {
      if (!this.searchStore.query) {
        this.history = [];
        return;
      }

      let baseHistory = this.searchStore.results || [];

      const clinicId = baseHistory[0]?.clinicId;
      if (clinicId && this.searchStore.query) {
        try {
          this.loading = true;
          const res = await AppointmentService.searchAppointment(
            this.searchStore.query,
            clinicId,
            null
          );
          baseHistory = res || [];
          this.searchStore.setResults(baseHistory);
        } catch (e) {
          const isCaptchaError =
            e?.message?.includes("Captcha is required") ||
            (Array.isArray(e?.graphQLErrors) &&
              e.graphQLErrors.some((gErr) =>
                gErr?.message?.includes("Captcha is required")
              ));

          if (
            isCaptchaError &&
            typeof window !== "undefined" &&
            window.grecaptcha &&
            process.env.VUE_APP_RECAPTCHA_SITE_KEY
          ) {
            try {
              const token = await new Promise((resolve, reject) => {
                window.grecaptcha.ready(() => {
                  window.grecaptcha
                    .execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                      action: "search",
                    })
                    .then(resolve)
                    .catch(reject);
                });
              });

              const resWithCaptcha = await AppointmentService.searchAppointment(
                this.searchStore.query,
                clinicId,
                token
              );
              baseHistory = resWithCaptcha || [];
              this.searchStore.setResults(baseHistory);
            } catch (captchaErr) {
              // eslint-disable-next-line no-console
              console.error(
                "Failed to refresh history with captcha token",
                captchaErr
              );
              baseHistory = this.searchStore.results || [];
            } finally {
              this.loading = false;
            }
          } else {
            // eslint-disable-next-line no-console
            console.error("Failed to refresh history from API", e);
            baseHistory = this.searchStore.results || [];
            this.loading = false;
          }
        } finally {
          if (this.loading) {
            this.loading = false;
          }
        }
      }

      this.history = await Promise.all(
        baseHistory.map(async (item) => {
          const petType =
            ANIMAL_TYPE.find((type) => type.id == item.petType)?.name || "";
          const petBreed =
            this.petBreedData.find((breed) => breed.id == item.petBreed)
              ?.label || "";
          const requests = item.requireIds.map(
            (request) =>
              this.requestData.find((req) => req.id == request)?.name || ""
          );

          let qrDataUrl = "";
          if (item.appointmentId) {
            try {
              qrDataUrl = await QRCode.toDataURL(String(item.appointmentId));
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("Failed to generate QR for history item", e);
            }
          }

          return {
            ...item,
            petType,
            petBreed,
            requests,
            qrDataUrl,
          };
        })
      );
    },
    downloadQR(qrDataUrl, appointmentId) {
      if (!qrDataUrl) return;
      const link = document.createElement("a");
      link.href = qrDataUrl;
      link.download = `qr-${appointmentId || "code"}.png`;
      link.click();
    },
    getTextStatus(status) {
      return status === STATUS.PENDING
        ? "Đang chờ"
        : status === STATUS.DENY
        ? "Từ chối"
        : status === STATUS.APPROVE ? "Đã xác nhận" : "Đang chờ";
    },
    formatDate(dateString) {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    },
    formatDayOnly(dateString) {
      return format(new Date(dateString), "dd/MM/yyyy");
    },
    handleMouseDown(e) {
      const tableWrapper = e.currentTarget;
      this.isDragging = true;
      this.startX = e.pageX - tableWrapper.offsetLeft;
      this.scrollLeft = tableWrapper.scrollLeft;
      tableWrapper.style.cursor = "grabbing";
      tableWrapper.style.userSelect = "none";
    },
    handleMouseMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const tableWrapper = e.currentTarget;
      const x = e.pageX - tableWrapper.offsetLeft;
      const walk = (x - this.startX) * 2; // Tốc độ scroll
      tableWrapper.scrollLeft = this.scrollLeft - walk;
    },
    handleMouseUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      const tableWrapper = e.currentTarget;
      tableWrapper.style.cursor = "grab";
      tableWrapper.style.userSelect = "";
    },
  },
  mounted() {
    // Set cursor mặc định
    const tableWrapper = this.$el.querySelector(".table-wrapper");
    if (tableWrapper) {
      tableWrapper.style.cursor = "grab";
    }
  },
};
</script>

<style scoped>
.history-container {
  max-width: 1340px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--white-color);
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  user-select: none;
}

.table-wrapper:active {
  cursor: grabbing;
}

.history-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: var(--white-color);
  min-width: 1800px;
  table-layout: fixed;
}

.history-table p {
  margin-bottom: 0;
  margin-top: 6px;
}

.history-table th {
  height: 20px;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 20px;
  color: #000000;
  white-space: nowrap;
  padding: 10px 20px;
}

.history-table thead {
  padding: 0px;
  height: 45px;
  background: #edf2f2;
  border-radius: 5px;
}

.history-table td {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #242424;
  padding: 20px 20px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.customer-info {
  font-weight: 600;
  font-size: 16px;
  line-height: 25px;
  display: flex;
  align-items: center;
  color: #242424;
  gap: 10px;
}

.history-table tbody tr td {
  vertical-align: top;
}

.history-table tbody tr {
  border-bottom: 1px solid #e0e0e0;
}

.customer-note {
  color: #77878f;
}

.customer-note span {
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 25px;
}

.status-pending {
  color: #009cfd !important;
}

.status-deny {
  color: #ff0000 !important;
}

.status-approve {
  color: #00dd4a !important;
}

.status-doing {
  color: #009cfd !important;
}

.status-approve {
  color: #00dd4a !important;
}

.status-not_arrived_yet {
  color: #009cfd !important;
}

.history-table thead tr th:first-child {
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.history-table thead tr th:last-child {
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}

/* Column widths */
.history-table th:nth-child(1),
.history-table td:nth-child(1) {
  width: 180px;
  min-width: 180px;
}

.history-table th:nth-child(2),
.history-table td:nth-child(2) {
  width: 180px;
  min-width: 180px;
}

.history-table th:nth-child(3),
.history-table td:nth-child(3) {
  width: 320px;
  min-width: 320px;
  padding-right: 25px !important;
  word-wrap: break-word;
  word-break: break-word;
}

.history-table th:nth-child(4),
.history-table td:nth-child(4) {
  width: 220px;
  min-width: 220px;
  padding-left: 25px !important;
  padding-right: 25px !important;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.5;
}

.history-table th:nth-child(5),
.history-table td:nth-child(5) {
  width: 280px;
  min-width: 280px;
  padding-left: 25px !important;
  padding-right: 10px !important;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.5;
}

.history-table th:nth-child(6),
.history-table td:nth-child(6) {
  width: 160px;
  min-width: 160px;
  padding-left: 10px !important;
  padding-right: 10px !important;
  text-align: center;
}

.history-table th:nth-child(7),
.history-table td:nth-child(7) {
  width: 150px;
  min-width: 150px;
  padding-left: 10px !important;
}

.qr-cell {
  text-align: center;
}

.qr-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: -10px;
}

.qr-image {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.qr-download {
  font-size: 16px;
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
}

.update-text {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  height: 22px;
  background: #ff7700;
  border-radius: 100px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 25px;
  color: #ffffff;
}

.history-mobile {
  display: none;
}

@media screen and (min-width: 768px) and (max-width: 835px) {
  .history-container {
    margin: 0 40px;
  }
}

@media screen and (max-width: 576px) {
  .history-mobile {
    display: block;
  }

  .history-table {
    display: none;
  }

  .history-container {
    /* margin: 0 20px; */
    margin-top: -40px;
    background-color: unset;
  }

  .main-container {
    padding-top: 20px !important;
  }
}

.history-mobile p {
  margin-bottom: 10px;
}

.mobile-label {
  font-weight: 600;
  font-size: 16px;
  line-height: 25px;
  color: #242424;
}

ul li {
  margin-bottom: 10px;
}

.history-mobile-item {
  margin-bottom: 20px;
  background-color: var(--white-color);
  padding: 20px;
  border-radius: 10px;
}

.btn-back {
  background-color: var(--primary-color);
  border: none;
}

.btn-back:hover,
.btn-back:active {
  background-color: var(--primary-color) !important;
  opacity: 0.8;
}
</style>
